import { create } from 'zustand';
import { apiClient } from '~/lib/apiClient';
import { useAuthStore } from './useAuthStore';

export interface Team {
  id: number;
  name: string;
  role: number;
  connections: {
    [key: string]: {
      sync_status:
        | 'synced'
        | 'failed'
        | 'syncing'
        | 'pending'
        | 'warning'
        | 'disabled';
      connection_status: 'incomplete' | 'complete' | 'pending';
      text: string;
    };
  };
  brandProfile: BrandProfile;
}

export interface BrandProfile {
  brandWebsite?: string;
  brandKeywords?: string[];
  brandDescription?: string;
  facebookPages?: {
    pageId?: string;
    userId?: string;
    pageToken?: string;
  };
  instagram?: {
    userId?: string;
    accessToken?: string;
  };
  hasLeadsTracking?: boolean;
  hasPerformanceTracking?: boolean;
  hasChannelPerformance?: boolean;
  hasBlinkitBrands?: boolean;
  hasStorePerformance?: boolean;
  custom_connector_overrides?: {
    [baseConnector: string]: string;
  };
}

export interface Member {
  id: number;
  email: string;
  name: string;
  role: string;
}

export interface Invitation {
  invitationId: number;
  email: string;
  role: string;
  invitedBy: string;
}

export interface Keyword {
  value: string;
  label: string;
}

interface TeamState {
  teamMembers: Member[];
  teamInvitations: Invitation[];
  teamKeywords: Keyword[];
  brandProfile: BrandProfile | null;
  loading: boolean;
  memberOperationLoading: boolean;
  memberOperationError: string | null;

  setTeamMembers: (members: Member[]) => void;
  setTeamInvitations: (invitations: Invitation[]) => void;
  setTeamKeywords: (keywords: Keyword[]) => void;
  setBrandProfile: (profile: BrandProfile | null) => void;
  setLoading: (loading: boolean) => void;
  setMemberOperationLoading: (loading: boolean) => void;
  setMemberOperationError: (error: string | null) => void;
  clearMemberOperationError: () => void;

  refreshTeamData: () => Promise<void>;
  inviteMember: (email: string, role: string) => Promise<void>;
  removeMember: (memberId: number) => Promise<void>;
  removeInvitation: (invitationId: number) => Promise<void>;
  updateBrandProfile: (data: Partial<BrandProfile>) => Promise<void>;
  updateTeamName: (name: string) => Promise<void>;
  cancelInvitation: (invitationId: number) => Promise<void>;
  addTeamMember: (email: string, role: string) => Promise<void>;
  updateMemberRole: (memberId: number, role: string) => Promise<void>;
  removeTeamMember: (memberId: number) => Promise<void>;
  updateTeamKeywords: (keywords: string[]) => Promise<void>;

  loadTeamData: () => Promise<void>;
  resetTeamData: () => void;
}

export const useTeamStore = create<TeamState>((set, get) => ({
  teamMembers: [],
  teamInvitations: [],
  teamKeywords: [],
  brandProfile: null,
  loading: false,
  memberOperationLoading: false,
  memberOperationError: null,

  setTeamMembers: (teamMembers) => set({ teamMembers }),
  setTeamInvitations: (teamInvitations) => set({ teamInvitations }),
  setTeamKeywords: (teamKeywords) => set({ teamKeywords }),
  setBrandProfile: (brandProfile) => set({ brandProfile }),
  setLoading: (loading) => set({ loading }),
  setMemberOperationLoading: (memberOperationLoading) =>
    set({ memberOperationLoading }),
  setMemberOperationError: (memberOperationError) =>
    set({ memberOperationError }),
  clearMemberOperationError: () => set({ memberOperationError: null }),

  resetTeamData: () => {
    set({
      teamMembers: [],
      teamInvitations: [],
      teamKeywords: [],
      brandProfile: null,
      loading: false,
    });
  },

  refreshTeamData: async () => {
    const authStore = useAuthStore.getState();
    if (!authStore.activeTeamId) return;

    try {
      const [members, invitationResponse] = await Promise.all([
        apiClient.get<Member[]>('/team/members'),
        apiClient.get<{ invitationDetails: Invitation[] }>('/invitation'),
      ]);

      const invitations = invitationResponse?.invitationDetails || [];
      set({
        teamMembers: members,
        teamInvitations: invitations,
      });
    } catch (err) {
      throw err;
    }
  },

  inviteMember: async (email: string, role: string) => {
    const authStore = useAuthStore.getState();
    try {
      set({ memberOperationLoading: true, memberOperationError: null });

      await apiClient.post('/invitation', {
        email,
        role: parseInt(role),
        team_id: authStore.activeTeamId,
      });

      const [invitationResponse, members] = await Promise.all([
        apiClient.get<{ invitationDetails: Invitation[] }>('/invitation'),
        apiClient.get<Member[]>('/team/members'),
      ]);

      const invitations = invitationResponse?.invitationDetails || [];
      set({
        teamInvitations: invitations,
        teamMembers: members,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to invite member';
      set({ memberOperationError: errorMessage });
      throw err;
    } finally {
      set({ memberOperationLoading: false });
    }
  },

  removeMember: async (memberId: number) => {
    try {
      set({ memberOperationLoading: true, memberOperationError: null });

      await apiClient.delete(`/team/members/${memberId}`);

      set((state) => ({
        teamMembers: state.teamMembers.filter(
          (member) => member.id !== memberId,
        ),
      }));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to remove member';
      set({ memberOperationError: errorMessage });
      throw err;
    } finally {
      set({ memberOperationLoading: false });
    }
  },

  removeInvitation: async (invitationId: number) => {
    try {
      await apiClient.delete(`/invitation/${invitationId}`);

      set((state) => ({
        teamInvitations: state.teamInvitations.filter(
          (inv) => inv.invitationId !== invitationId,
        ),
      }));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to remove invitation';
      throw new Error(errorMessage);
    }
  },

  updateBrandProfile: async (data: Partial<BrandProfile>) => {
    try {
      const backendData: any = {};

      if (data.brandWebsite !== undefined) {
        backendData.website = data.brandWebsite;
      }
      if (data.brandDescription !== undefined) {
        backendData.description = data.brandDescription;
      }
      if (data.brandKeywords !== undefined) {
        backendData.keywords = data.brandKeywords;
      }
      Object.keys(data).forEach((key) => {
        if (
          !['brandWebsite', 'brandDescription', 'brandKeywords'].includes(key)
        ) {
          backendData[key] = (data as any)[key];
        }
      });

      await apiClient.put('/brand-profile', backendData);

      set((state) => ({
        brandProfile: { ...state.brandProfile, ...data },
      }));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update brand profile';
      throw new Error(errorMessage);
    }
  },

  updateTeamName: async (name: string) => {
    try {
      await apiClient.patch('/team', { team_name: name });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update team name';
      throw new Error(errorMessage);
    }
  },

  cancelInvitation: async (invitationId: number) => {
    return get().removeInvitation(invitationId);
  },

  addTeamMember: async (email: string, role: string) => {
    return get().inviteMember(email, role);
  },

  updateMemberRole: async (memberId: number, role: string) => {
    try {
      set({ memberOperationLoading: true, memberOperationError: null });

      await apiClient.patch(`/team/members/${memberId}`, {
        role: parseInt(role),
      });

      await get().refreshTeamData();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update member role';
      throw new Error(errorMessage);
    } finally {
      set({ memberOperationLoading: false });
    }
  },

  removeTeamMember: async (memberId: number) => {
    return get().removeMember(memberId);
  },

  updateTeamKeywords: async (keywords: string[]) => {
    try {
      await apiClient.put('/brand-profile', { keywords });

      const keywordObjects: Keyword[] = keywords.map((kw) => ({
        value: kw,
        label: kw ? kw.charAt(0).toUpperCase() + kw.slice(1) : '',
      }));

      set({ teamKeywords: keywordObjects });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update team keywords';
      throw new Error(errorMessage);
    }
  },

  loadTeamData: async () => {
    const authStore = useAuthStore.getState();

    if (!authStore.activeTeamId) {
      get().resetTeamData();
      return;
    }

    set({
      loading: true,
      teamMembers: [],
      teamInvitations: [],
      teamKeywords: [],
      brandProfile: null,
    });

    try {
      const [
        membersResult,
        invitationResult,
        brandProfileResult,
        connectionsResult,
      ] = await Promise.allSettled([
        apiClient.get<Member[]>('/team/members'),
        apiClient.get<{ invitationDetails: Invitation[] }>('/invitation'),
        apiClient.get<{ detail?: string } | BrandProfile>('/brand-profile'),
        apiClient.get<Team['connections']>('/team/connections'),
      ]);

      const members =
        membersResult.status === 'fulfilled' ? membersResult.value || [] : [];
      const invitations =
        invitationResult.status === 'fulfilled'
          ? invitationResult.value?.invitationDetails || []
          : [];
      const brandProfileResponse =
        brandProfileResult.status === 'fulfilled'
          ? brandProfileResult.value
          : null;
      const connections =
        connectionsResult.status === 'fulfilled'
          ? connectionsResult.value || {}
          : {};

      if (
        membersResult.status === 'rejected' ||
        invitationResult.status === 'rejected' ||
        brandProfileResult.status === 'rejected'
      ) {
        const failed =
          membersResult.status === 'rejected'
            ? membersResult.reason
            : invitationResult.status === 'rejected'
              ? invitationResult.reason
              : brandProfileResult.status === 'rejected'
                ? brandProfileResult.reason
                : null;
        throw failed instanceof Error
          ? failed
          : new Error('Failed to load team data');
      }

      let profileData: BrandProfile | null = null;
      let keywords: Keyword[] = [];

      if (brandProfileResponse && !('detail' in brandProfileResponse)) {
        const backendProfile = brandProfileResponse as any;

        profileData = {
          brandWebsite: backendProfile.website || backendProfile.brandWebsite,
          brandDescription:
            backendProfile.description || backendProfile.brandDescription,
          brandKeywords:
            backendProfile.keywords || backendProfile.brandKeywords,
          facebookPages: backendProfile.facebookPages,
          instagram: backendProfile.instagram,
          hasLeadsTracking: backendProfile.hasLeadsTracking,
          hasPerformanceTracking: backendProfile.hasPerformanceTracking,
          hasChannelPerformance: backendProfile.hasChannelPerformance,
          hasBlinkitBrands: backendProfile.hasBlinkitBrands,
          ...(typeof backendProfile.hasStorePerformance === 'boolean'
            ? { hasStorePerformance: backendProfile.hasStorePerformance }
            : {}),
          custom_connector_overrides: backendProfile.custom_connector_overrides,
        };

        keywords = (
          backendProfile.keywords ||
          backendProfile.brandKeywords ||
          []
        ).map((kw: string) => ({
          value: kw,
          label: kw ? kw.charAt(0).toUpperCase() + kw.slice(1) : '',
        }));
      }

      const activeTeamId = authStore.activeTeamId;
      useAuthStore.setState((state) => ({
        teams: state.teams.map((team) =>
          team.id === activeTeamId
            ? {
                ...team,
                connections:
                  Object.keys(connections).length > 0
                    ? connections
                    : team.connections,
                brandProfile: profileData
                  ? { ...team.brandProfile, ...profileData }
                  : team.brandProfile,
              }
            : team,
        ),
      }));

      set({
        teamMembers: members,
        teamInvitations: invitations,
        teamKeywords: keywords || [],
        brandProfile: profileData,
      });
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : 'Failed to load team data',
      );
    } finally {
      set({ loading: false });
    }
  },
}));

export const useActiveTeam = () => {
  return useAuthStore(
    (state) =>
      state.teams.find((team) => team.id === state.activeTeamId) || null,
  );
};
